// Record a session: a tab in the top bar that slides the recorder deck out
// beneath it (engine: recorder/engine.ts; sound display: recorder/synthwave.ts).
//
// It knows the AE meeting the user is in right now (from their synced Outlook
// calendar) and offers to record it; outside a meeting it records for the
// project open on screen, or one picked from a list. The recording goes to
// the user's Speakr account; the transcript then lands in the project's
// Chronologie. While a recording runs the tab shows the time and a small live
// meter, so the deck can be closed and the recording goes on.
import { useEffect, useRef, useState, useCallback, useSyncExternalStore, type ReactElement, type ReactNode } from 'react';
import { Mic, Pause, Play, Square, Trash2, Loader2, Check, AlertTriangle, X } from 'lucide-react';
import { useT } from '../../i18n';
import { api } from '../../api/client';
import {
  getRecorderState, subscribeRecorder, startRecording, pauseRecording, resumeRecording, stopRecording,
  discardRecording, clearRecorderError, finishOrphan, discardOrphan, retryHandover,
  recorderSupported, onlineAudioSupported, getRecorderAnalyser, listMicrophones, getPreferredMic, setPreferredMic,
  restartWithMicrophone, reportSilence, type AudioSource, type Microphone, type RecorderPhase,
} from '../../recorder/engine';
import { Meter, ScreenPainter, voiceBands, readBands, drawMini, type ScreenMode } from '../../recorder/synthwave';

interface ProjectRef { id: number; project_code: string | null; name: string }
interface Meeting { calendar_event_id: number; subject: string; start_time: string; end_time: string; project_id: number; project_code: string | null; project_name: string }
interface Rec { id: number; project_id: number | null; title: string; status: string; started_at: string; chunk_count: number; error: string | null }
interface Context { connected: boolean; meeting: Meeting | null; pageProject: ProjectRef | null; active: Rec | null; latest: Rec | null; projects: ProjectRef[] }

const code = (p: { project_code: string | null } | null | undefined) => p?.project_code ? p.project_code.toUpperCase() : null;
const hhmm = (iso: string) => new Date(iso).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
function clock(ms: number) {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}
const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

type Silence = null | 'never' | 'pause';
const SILENCE_MS = 6000;

/**
 * Watches the recorded sound, deck open or not: a long silence before any
 * sound means the microphone hears nothing; after some sound, a pause in the talk.
 */
function useSilenceWatch(phase: RecorderPhase, recordingId: number | null, onSilence: (s: Silence) => void) {
  const cb = useRef(onSilence);
  cb.current = onSilence;
  // Survives pause/resume: a sound heard once means a silence later is a pause in the talk.
  const heard = useRef(false);
  useEffect(() => { heard.current = false; }, [recordingId]);
  useEffect(() => {
    if (phase !== 'recording') return;
    const an = getRecorderAnalyser();
    if (!an) return;
    const edges = voiceBands(an, 28);
    const bins = new Uint8Array(an.frequencyBinCount);
    const bands = new Float32Array(28);
    let loudAt = performance.now();
    let silent: Silence = null;
    const id = setInterval(() => {
      const now = performance.now();
      if (readBands(an, bins, edges, bands) > 0.3) { loudAt = now; heard.current = true; }
      const s: Silence = now - loudAt > SILENCE_MS ? (heard.current ? 'pause' : 'never') : null;
      if (s !== silent) { silent = s; cb.current(s); }
    }, 150);
    return () => { clearInterval(id); if (silent) cb.current(null); };
  }, [phase]);
}

/** Size a canvas's pixels to its box at the screen's density. */
function fitPixels(canvas: HTMLCanvasElement, w: number, h: number, dpr: number) {
  const cw = Math.round(w * dpr);
  const ch = Math.round(h * dpr);
  if (canvas.width !== cw || canvas.height !== ch) { canvas.width = cw; canvas.height = ch; }
}

const SCREEN_BARS = 20;

/** The deck's screen. Paints at the display's own rate, only while the deck is open. */
function SynthScreen({ phase, open }: { phase: RecorderPhase; open: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const meter = useRef(new Meter(SCREEN_BARS));
  const painter = useRef<ScreenPainter | null>(null);
  // The grid's travel and speed carry over pause and resume, so it never jumps.
  const motion = useRef({ scroll: 0, speed: 0 });
  useEffect(() => {
    const canvas = ref.current;
    const g = canvas?.getContext('2d');
    if (!canvas || !g || !open) return;
    painter.current ??= new ScreenPainter();
    const live = phase === 'recording';
    const an = live ? getRecorderAnalyser() : null;
    const edges = an ? voiceBands(an, SCREEN_BARS) : [];
    const bins = an ? new Uint8Array(an.frequencyBinCount) : null;
    const raw = new Float32Array(SCREEN_BARS);
    const mode: ScreenMode = live ? 'live' : phase === 'paused' ? 'paused' : 'idle';
    const still = reducedMotion();
    // Measured by an observer, not read each frame (a read can force a layout mid-animation).
    let size = { w: canvas.clientWidth, h: canvas.clientHeight };
    const ro = new ResizeObserver(([e]) => { size = { w: e.contentRect.width, h: e.contentRect.height }; });
    ro.observe(canvas);
    let raf = 0;
    let last = 0;
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const dt = last ? Math.min(now - last, 100) : 16;
      last = now;
      const { w, h } = size;
      if (!w || !h) return;
      const dpr = window.devicePixelRatio || 1;
      fitPixels(canvas, w, h, dpr);
      if (an && bins) readBands(an, bins, edges, raw);
      meter.current.step(an ? raw : null, dt);
      // The grid eases towards a speed that follows the sound over about a second.
      const m = motion.current;
      const target = still ? 0 : mode === 'live' ? 0.5 + 0.8 * meter.current.energy : mode === 'idle' ? 0.18 : 0;
      m.speed += (target - m.speed) * Math.min(1, dt / 900);
      m.scroll += (dt / 1000) * m.speed;
      painter.current!.paint(g, w, h, dpr, meter.current, mode, now / 1000, m.scroll);
    };
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [phase, open]);
  return <canvas ref={ref} className="rec-screen-canvas" aria-hidden />;
}

/** The tab's live meter while recording. */
function TabMeter({ phase }: { phase: RecorderPhase }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const meter = useRef(new Meter(5));
  useEffect(() => {
    const canvas = ref.current;
    const g = canvas?.getContext('2d');
    if (!canvas || !g) return;
    const an = phase === 'recording' ? getRecorderAnalyser() : null;
    const edges = an ? voiceBands(an, 5) : [];
    const bins = an ? new Uint8Array(an.frequencyBinCount) : null;
    const raw = new Float32Array(5);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    let raf = 0;
    let last = 0;
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const dt = now - last;
      if (last && dt < 45) return; // a level meter needs no more than ~20 frames a second
      last = now;
      const dpr = window.devicePixelRatio || 1;
      fitPixels(canvas, w, h, dpr);
      if (an && bins) readBands(an, bins, edges, raw);
      meter.current.step(an ? raw : null, Math.min(dt, 100));
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawMini(g, w, h, meter.current, phase !== 'recording');
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [phase]);
  return <canvas ref={ref} className="rec-tab-meter" aria-hidden />;
}

export function SessionRecorder({ currentProjectId }: { currentProjectId: number | null }) {
  const { t } = useT();
  const rs = useSyncExternalStore(subscribeRecorder, getRecorderState);
  const [ctx, setCtx] = useState<Context | null>(null);
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState<AudioSource>('room');
  const [picking, setPicking] = useState(false);
  const [projects, setProjects] = useState<ProjectRef[] | null>(null);
  const [pickedId, setPickedId] = useState<number | null>(null);
  const [, tick] = useState(0);
  const [noSound, setNoSound] = useState<Silence>(null);
  const [mics, setMics] = useState<Microphone[]>([]);
  const [micId, setMicId] = useState<string | null>(() => getPreferredMic());
  const silenceReported = useRef<number | null>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const tabRef = useRef<HTMLButtonElement>(null);

  useSilenceWatch(rs.phase, rs.recordingId, setNoSound);

  // The microphone list (names come once the site may use the microphone; refreshed when one is plugged in).
  useEffect(() => {
    let alive = true;
    const refresh = () => { void listMicrophones().then(m => { if (alive) setMics(m); }); };
    refresh();
    navigator.mediaDevices?.addEventListener?.('devicechange', refresh);
    return () => { alive = false; navigator.mediaDevices?.removeEventListener?.('devicechange', refresh); };
  }, [rs.phase === 'recording']);

  useEffect(() => { setNoSound(null); }, [rs.recordingId]);
  useEffect(() => {
    if (noSound === 'never' && rs.recordingId && silenceReported.current !== rs.recordingId) {
      silenceReported.current = rs.recordingId;
      void reportSilence();
      setOpen(true); // the deck says what to do about it
    }
  }, [noSound, rs.recordingId]);
  useEffect(() => { if (rs.error) setOpen(true); }, [rs.error]);

  // Close on Escape or a click elsewhere; the recording goes on.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); tabRef.current?.focus(); } };
    const onDown = (e: MouseEvent) => { if (!dockRef.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [open]);

  const load = useCallback(async () => {
    try {
      const q = currentProjectId ? `?projectId=${currentProjectId}` : '';
      const res = await fetch(`/api/recordings/context${q}`, { credentials: 'include' });
      if (res.ok) setCtx(await res.json());
    } catch { /* keep the last context */ }
  }, [currentProjectId]);

  // Refresh on page change, every minute, and every 15 s while a hand-over or transcription runs.
  const busy = ctx?.latest && ['uploading', 'processing'].includes(ctx.latest.status);
  useEffect(() => { void load(); }, [load, rs.phase]);
  useEffect(() => {
    const id = setInterval(load, busy ? 15_000 : 60_000);
    return () => clearInterval(id);
  }, [load, busy]);

  // The clock.
  useEffect(() => {
    if (rs.phase !== 'recording') return;
    const id = setInterval(() => tick(n => n + 1), 500);
    return () => clearInterval(id);
  }, [rs.phase]);

  const meeting = ctx?.meeting || null;
  const target: ProjectRef | null = meeting
    ? { id: meeting.project_id, project_code: meeting.project_code, name: meeting.project_name }
    : ctx?.pageProject || null;
  const running = rs.phase === 'recording' || rs.phase === 'paused' || rs.phase === 'stopping' || rs.phase === 'starting';
  const orphan = !running && ctx?.active ? ctx.active : null;
  // Nothing to suggest: the deck asks for the project straight away.
  const showPicker = !running && !orphan && !!ctx?.connected && !meeting && (picking || !target);

  useEffect(() => {
    if (!open || !showPicker || projects) return;
    api.getProjects().then((list: any) => {
      const rows = (Array.isArray(list) ? list : list?.projects || []) as any[];
      setProjects(rows
        .filter(p => p.access_level !== 'card')
        .map(p => ({ id: p.id, project_code: p.project_code, name: p.name }))
        .sort((a, b) => (code(a) || 'ZZ' + a.name).localeCompare(code(b) || 'ZZ' + b.name, 'fr', { numeric: true })));
    }).catch(() => setProjects([]));
  }, [open, showPicker, projects]);

  if (!recorderSupported()) return null;

  const projectOf = (id: number | null) => ctx?.projects.find(p => p.id === id) || null;
  const elapsed = rs.elapsedMs + (rs.runningSince ? Date.now() - rs.runningSince : 0);

  const start = (projectId: number, calendarEventId: number | null) => {
    setPicking(false);
    void startRecording({ projectId, calendarEventId, audioSource: source, deviceId: mics.some(m => m.deviceId === micId) ? micId : null });
  };

  const sourcePicker = onlineAudioSupported() && (
    <div className="rec-field">
      <span className="rec-field-label">{t('recorder.sourceLabel')}</span>
      <div className="rec-source" role="radiogroup" aria-label={t('recorder.sourceLabel')}>
        <button type="button" role="radio" aria-checked={source === 'room'} className={source === 'room' ? 'is-on' : ''} onClick={() => setSource('room')}>
          {t('recorder.sourceRoom')}
        </button>
        <button type="button" role="radio" aria-checked={source === 'online'} className={source === 'online' ? 'is-on' : ''} onClick={() => setSource('online')} title={t('recorder.sourceOnlineHelp')}>
          {t('recorder.sourceOnline')}
        </button>
      </div>
    </div>
  );

  // Only worth showing when there is a choice; the browser's default otherwise.
  const realMics = mics.filter(m => m.deviceId !== 'default');
  const chosenMic = realMics.some(m => m.deviceId === micId) ? micId! : '';
  const micPicker = realMics.length > 1 && (
    <label className="rec-field">
      <span className="rec-field-label">{t('recorder.mic')}</span>
      <select
        className="form-select rec-select"
        value={chosenMic}
        onChange={e => { const v = e.target.value || null; setMicId(v); setPreferredMic(v); }}
      >
        <option value="">{t('recorder.micDefault')}</option>
        {realMics.map(m => <option key={m.deviceId} value={m.deviceId}>{m.label}</option>)}
      </select>
    </label>
  );

  const errorLine = rs.error && (
    <div className="rec-error" role="alert">
      <AlertTriangle size={14} /> <span>{t(`recorder.error.${rs.error}`)}{rs.errorDetail && <small className="rec-error-detail">{rs.errorDetail}</small>}</span>
      <button type="button" className="rec-link" onClick={clearRecorderError}>{t('recorder.dismiss')}</button>
    </div>
  );

  // The project line; `aside` sits at its right end (the switch to another project).
  const what = (p: ProjectRef | null, title?: string | null, aside?: ReactNode) => (code(p) || title || p) && (
    <div className="rec-what">
      {code(p) && <span className="rec-code">{code(p)}</span>}
      <span className="rec-title">{title || p?.name}</span>
      {aside}
    </div>
  );

  // What the screen says, top left.
  let hud = t('recorder.ready');
  let hudClass = '';
  if (running) {
    hud = rs.phase === 'starting' ? t('recorder.starting') : rs.phase === 'stopping' ? t('recorder.sending') : rs.phase === 'paused' ? t('recorder.paused') : t('recorder.recording');
    hudClass = rs.phase === 'recording' ? ' is-live' : '';
  } else if (orphan) {
    hud = t('recorder.orphan'); hudClass = ' is-warn';
  } else if (meeting) {
    hud = t('recorder.inMeeting'); hudClass = ' is-meeting';
  }

  let body: ReactElement;

  if (running) {
    const p = projectOf(rs.projectId) || (target?.id === rs.projectId ? target : null);
    body = (
      <>
        {what(p, rs.title?.replace(/^\d{8}\s+(ae\d+\s+)?/i, ''))}
        {noSound === 'pause' && rs.phase === 'recording' && <div className="rec-note">{t('recorder.noSound')}</div>}
        {noSound === 'never' && rs.phase === 'recording' && (
          <div className="rec-note rec-nosound">
            <span>{t('recorder.noSoundAtAll', { mic: rs.micLabel || t('recorder.micUnknown') })}</span>
            {realMics.length > 1 && (
              <select
                className="form-select rec-select"
                value=""
                onChange={e => { if (e.target.value) { setMicId(e.target.value); void restartWithMicrophone(e.target.value); } }}
              >
                <option value="">{t('recorder.micSwitch')}</option>
                {realMics.filter(m => m.label !== rs.micLabel).map(m => <option key={m.deviceId} value={m.deviceId}>{m.label}</option>)}
              </select>
            )}
            {/Mac/.test(navigator.userAgent) && <small className="rec-muted">{t('recorder.macMicHint')}</small>}
          </div>
        )}
        {rs.onlineAudioLost && <div className="rec-note">{t('recorder.onlineLost')}</div>}
        {rs.offline && <div className="rec-note">{t('recorder.offline', { n: rs.pending })}</div>}
        {rs.micLabel && rs.phase !== 'stopping' && <div className="rec-muted rec-mic-now"><Mic size={12} /> {rs.micLabel}</div>}
        {rs.phase === 'stopping' ? (
          <div className="rec-actions"><Loader2 size={15} className="rec-spin" /> <span className="rec-muted">{t('recorder.sendingHelp')}</span></div>
        ) : rs.phase !== 'starting' && (
          <div className="rec-actions rec-actions--live">
            {rs.phase === 'paused' ? (
              <button type="button" className="rec-btn" onClick={resumeRecording}><Play size={14} /> {t('recorder.resume')}</button>
            ) : (
              <button type="button" className="rec-btn" onClick={pauseRecording}><Pause size={14} /> {t('recorder.pause')}</button>
            )}
            <button type="button" className="rec-btn rec-btn--hot" onClick={() => void stopRecording()}><Square size={12} /> {t('recorder.stop')}</button>
            <button
              type="button"
              className="rec-icon-btn"
              title={t('recorder.discard')}
              aria-label={t('recorder.discard')}
              onClick={() => { if (window.confirm(t('recorder.discardConfirm'))) void discardRecording(); }}
            >
              <Trash2 size={15} />
            </button>
          </div>
        )}
      </>
    );
  } else if (orphan) {
    const p = projectOf(orphan.project_id);
    body = (
      <>
        {what(p)}
        <div className="rec-muted">{t('recorder.orphanHelp', { n: Math.max(1, Math.round(orphan.chunk_count * 5 / 60)) })}</div>
        <div className="rec-actions">
          <button type="button" className="rec-btn rec-btn--hot" onClick={() => finishOrphan(orphan.id).then(load)}>{t('recorder.orphanFinish')}</button>
          <button type="button" className="rec-btn" onClick={() => { if (window.confirm(t('recorder.discardConfirm'))) void discardOrphan(orphan.id).then(load); }}>{t('recorder.discardShort')}</button>
        </div>
      </>
    );
  } else if (!ctx) {
    body = <div className="rec-muted"><Loader2 size={14} className="rec-spin" /> {t('recorder.loading')}</div>;
  } else if (!ctx.connected) {
    body = (
      <>
        <div className="rec-muted">{t('recorder.notConnected')}</div>
        <a className="rec-link" href="#profile" onClick={() => setOpen(false)}>{t('recorder.connect')}</a>
      </>
    );
  } else if (meeting) {
    body = (
      <>
        {what(target, meeting.subject.replace(/(?<![\w-])ae\d+\b/i, '').replace(/^[\s:·\-–—]+/, '') || meeting.project_name)}
        <div className="rec-muted">{t('recorder.meetingTime', { from: hhmm(meeting.start_time), to: hhmm(meeting.end_time) })}</div>
        {sourcePicker}
        {micPicker}
        <button type="button" className="rec-btn rec-btn--go" onClick={() => start(meeting.project_id, meeting.calendar_event_id)}>
          <span className="rec-go-dot" aria-hidden /> {t('recorder.recordMeeting')}
        </button>
      </>
    );
  } else if (showPicker) {
    body = (
      <>
        <div className="rec-field">
          {/* "Annuler" takes the place of "Autre projet", so the switch stays where it was */}
          <div className="rec-field-head">
            <span className="rec-field-label" id="rec-pick-label">{t('recorder.pickProject')}</span>
            {target && <button type="button" className="rec-link" onClick={() => setPicking(false)}>{t('recorder.cancel')}</button>}
          </div>
          <select className="form-select rec-select" aria-labelledby="rec-pick-label" value={pickedId ?? ''} onChange={e => setPickedId(Number(e.target.value) || null)}>
            <option value="">{projects ? t('recorder.pickPlaceholder') : t('recorder.loading')}</option>
            {(projects || []).map(p => <option key={p.id} value={p.id}>{code(p) ? `${code(p)} · ` : ''}{p.name}</option>)}
          </select>
        </div>
        {sourcePicker}
        {micPicker}
        <button type="button" className="rec-btn rec-btn--go" disabled={!pickedId} onClick={() => pickedId && start(pickedId, null)}>
          <span className="rec-go-dot" aria-hidden /> {t('recorder.record')}
        </button>
      </>
    );
  } else {
    body = (
      <>
        {what(target, null, <button type="button" className="rec-link" onClick={() => setPicking(true)}>{t('recorder.otherProject')}</button>)}
        {sourcePicker}
        {micPicker}
        <button type="button" className="rec-btn rec-btn--go" onClick={() => target && start(target.id, null)}>
          <span className="rec-go-dot" aria-hidden /> {t('recorder.recordSession')}
        </button>
      </>
    );
  }

  // What happened to the last recording.
  const latest = !running && ctx?.latest;
  const lp = latest ? projectOf(latest.project_id) : null;
  const status = latest && (
    <div className={`rec-status rec-status--${latest.status}`}>
      {latest.status === 'uploading' && <><Loader2 size={13} className="rec-spin" /> <span>{t('recorder.status.uploading')}</span></>}
      {latest.status === 'processing' && <><Loader2 size={13} className="rec-spin" /> <span>{t('recorder.status.processing', { code: code(lp) || '' })}</span></>}
      {latest.status === 'completed' && (
        <><Check size={13} /> <span>{t('recorder.status.completed')}</span>
          {latest.project_id && <a className="rec-link" href={`#project?id=${latest.project_id}&tab=apercu`} onClick={() => setOpen(false)}>{t('recorder.open')}</a>}</>
      )}
      {latest.status === 'failed' && (
        <><AlertTriangle size={13} /> <span>{t('recorder.status.failed')}</span>
          <button type="button" className="rec-link" onClick={() => retryHandover(latest.id).then(load)}>{t('recorder.retry')}</button></>
      )}
    </div>
  );

  // The tab in the top bar.
  const badge = latest && (
    latest.status === 'failed' ? <AlertTriangle size={12} className="rec-tab-badge is-failed" />
      : ['uploading', 'processing'].includes(latest.status) ? <Loader2 size={12} className="rec-tab-badge rec-spin" />
      : null
  );
  const tabState = running ? (rs.phase === 'recording' ? ' is-live' : ' is-held') : meeting ? ' is-meeting' : '';
  const tab = (
    <button
      ref={tabRef}
      type="button"
      className={`rec-tab${tabState}${noSound === 'never' ? ' is-warn' : ''}`}
      aria-expanded={open}
      aria-controls="rec-deck"
      title={running ? t('recorder.recording') : meeting ? t('recorder.inMeeting') : t('recorder.openDeck')}
      onClick={() => setOpen(o => !o)}
    >
      {running ? (
        <>
          <span className="rec-tab-dot" aria-hidden />
          {rs.phase !== 'starting' && <TabMeter phase={rs.phase} />}
          <span className="rec-tab-clock">{rs.phase === 'starting' ? '··:··' : clock(elapsed)}</span>
        </>
      ) : meeting ? (
        <>
          <span className="rec-tab-dot" aria-hidden />
          <span className="rec-tab-label">{code(meeting) || t('recorder.inMeeting')}</span>
        </>
      ) : (
        <>
          <Mic size={15} />
          <span className="rec-tab-label">{t('recorder.record')}</span>
        </>
      )}
      {badge}
    </button>
  );

  return (
    <div ref={dockRef} className={`rec-dock${open ? ' is-open' : ''}`}>
      {tab}
      <div className="rec-flap">
        <section id="rec-deck" className="rec-deck" role="dialog" aria-label={t('recorder.deck')} aria-hidden={!open}>
          <div className="rec-screen">
            <SynthScreen phase={rs.phase} open={open} />
            <div className="rec-hud">
              <span className={`rec-hud-state${hudClass}`}>{hud}</span>
              {running && <span className="rec-hud-clock">{clock(elapsed)}</span>}
            </div>
            <button type="button" className="rec-close" aria-label={t('recorder.close')} onClick={() => { setOpen(false); tabRef.current?.focus(); }}>
              <X size={15} />
            </button>
          </div>
          <div className="rec-body">
            {errorLine}
            {body}
            {status}
          </div>
        </section>
      </div>
    </div>
  );
}
